import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { ApiEnvelope, ConsigneeDTO, ImportPreview, ImportResult } from './types';
import { normalizeNpwp } from './npwp';
import { consigneeKeys } from './keys';
import { broadcastConsigneeChange } from './live';

export { consigneeKeys };

const fetchConsignees = async (query: string, limit: number, signal?: AbortSignal): Promise<ConsigneeDTO[]> => {
  const search = new URLSearchParams({ offset: '0', limit: String(limit) });
  if (query) search.set('query', query);
  const response = await axiosInstance.get<ApiEnvelope<ConsigneeDTO[]>>(
    `${apiUrl.master.consignees.base}?${search.toString()}`,
    { signal }
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

/**
 * The PPJK's consignee whose NPWP equals the given one, or null.
 *
 * The list endpoint matches NPWP digits as a substring, so the exact match is
 * settled here. One request per distinct normalised NPWP per session: the key
 * is the 16-digit form, so "0123" typed twice with punctuation hits the cache.
 */
const lookupConsignee = async (npwp16: string, signal?: AbortSignal): Promise<ConsigneeDTO | null> => {
  const rows = await fetchConsignees(npwp16, 5, signal);
  return rows.find((row) => normalizeNpwp(row.npwp).normalized === npwp16 && row.isActive !== false) ?? null;
};

const LOOKUP_STALE_MS = 5 * 60 * 1000;

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/** 403/404: the user may not read the master, or it is not there. Retrying or refocusing will not change that. */
const isAccessDenied = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === 403 || status === 404;
};

const retryUnlessDenied = (failureCount: number, error: unknown): boolean => !isAccessDenied(error) && failureCount < 1;

export function useConsigneeLookupByNpwp(npwp16: string | null) {
  return useQuery({
    queryKey: consigneeKeys.lookup(npwp16 ?? ''),
    queryFn: ({ signal }): Promise<ConsigneeDTO | null> =>
      npwp16 ? lookupConsignee(npwp16, signal) : Promise.resolve(null),
    enabled: Boolean(npwp16),
    staleTime: LOOKUP_STALE_MS,
    // The app default is off, and 'always' ignores the 5-minute staleTime: a
    // draft must notice master edits made in another tab. Not while the last
    // answer was "no access": every focus would just repeat the 403.
    refetchOnWindowFocus: (query) =>
      query.state.status === 'error' && isAccessDenied(query.state.error) ? false : 'always',
    retry: retryUnlessDenied,
  });
}

/** Same lookup, awaited imperatively (the NPWP sync button). Shares the query cache. */
export const fetchConsigneeByNpwp = (queryClient: QueryClient, npwp16: string): Promise<ConsigneeDTO | null> =>
  queryClient.fetchQuery({
    queryKey: consigneeKeys.lookup(npwp16),
    queryFn: ({ signal }) => lookupConsignee(npwp16, signal),
    staleTime: LOOKUP_STALE_MS,
  });

/** Search by nama or NPWP for the picker. Empty query returns the newest rows. */
export function useSearchConsignees(query: string, options?: { enabled?: boolean }) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: consigneeKeys.search(trimmed),
    queryFn: ({ signal }) => fetchConsignees(trimmed, 20, signal),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: (query) => !(query.state.status === 'error' && isAccessDenied(query.state.error)),
    retry: retryUnlessDenied,
    placeholderData: (previous) => previous,
  });
}

const buildForm = (file: File): FormData => {
  const form = new FormData();
  form.append('file', file);
  return form;
};

/** Parses and classifies the sheet without writing anything. */
export function usePreviewConsigneeImport() {
  return useMutation({
    mutationFn: async (file: File): Promise<ImportPreview> => {
      const response = await axiosInstance.post<ApiEnvelope<ImportPreview>>(
        `${apiUrl.master.consignees.import}?dryRun=true`,
        buildForm(file),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data;
    },
  });
}

/** Applies the same sheet. The server re-parses, so the preview is never stale. */
export function useApplyConsigneeImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ImportResult> => {
      const response = await axiosInstance.post<ApiEnvelope<ImportResult>>(
        apiUrl.master.consignees.import,
        buildForm(file),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: consigneeKeys.all });
      broadcastConsigneeChange();
    },
  });
}

/** Reads the server's error envelope; falls back to the transport message. */
export const importErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
  return maybe?.response?.data?.error?.message || maybe?.response?.data?.message || maybe?.message || fallback;
};
